import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Import components
import HeaderMain from './components/HeaderMain';
import Banner from './components/Banner';
import About from './components/About';
import Services from './components/Services';
import Projects from './components/Projects';
import Team from './components/Team';
import WhyChoose from './components/WhyChoose';
import Testimonials from './components/Testimonials';
import Blog from './components/Blog';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Preloader from './components/Preloader';
import Article from './components/Article';

// Create theme based on original website colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#bb0b0b', // Main red color from original site
      dark: '#a30a0a',
      light: '#d41212'
    },
    secondary: {
      main: '#043d72', // Secondary blue color from original site
      dark: '#032c53',
      light: '#0a4e8c'
    },
    background: {
      default: '#ffffff',
      paper: '#f9f9f9',
      dark: '#101010'
    },
    text: {
      primary: '#0a0a0a',
      secondary: '#454545',
      light: '#ffffff'
    },
  },
  typography: {
    fontFamily: '"Poppins", sans-serif',
    h1: {
      fontSize: '52px',
      lineHeight: '62px',
      fontWeight: 700,
    },
    h2: {
      fontSize: '40px',
      lineHeight: '50px',
      fontWeight: 700,
    },
    h3: {
      fontSize: '28px',
      lineHeight: '42px',
      fontWeight: 700,
    },
    h4: {
      fontSize: '26px',
      lineHeight: '36px',
      fontWeight: 700,
    },
    h5: {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 600,
    },
    h6: {
      fontSize: '20px',
      lineHeight: '28px',
      fontWeight: 500,
    },
    body1: {
      fontSize: '15px',
      lineHeight: 1.8,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '30px',
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

const MainContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
}));

// ScrollToTopOnNavigate component to scroll to top when route changes
const ScrollToTopOnNavigate = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return null;
};

// HomePage component to contain all the sections
const HomePage = () => (
  <>
    <HeaderMain />
    <Banner />
    <About />
    <Services />
    <Projects />
    <Team />
    <WhyChoose />
    <Testimonials />
    <Blog />
    <Contact />
    <Footer />
    <ScrollToTop />
  </>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Preloader />
      <Router>
        <MainContent>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/article/:id" element={<><ScrollToTopOnNavigate /><Article /></>} />
          </Routes>
        </MainContent>
      </Router>
    </ThemeProvider>
  );
}

export default App;
